import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calculator, TrendingUp, DollarSign, Target, ArrowRight, ArrowLeft, Sparkles, BarChart3, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { AnimatedCounter } from "@/components/ui/AnimatedCounter";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

interface FormData {
  services: string[];
  monthlyAdSpend: number;
  monthlyRevenue: number;
  monthlyLeads: number;
  conversionRate: number;
  avgCustomerValue: number;
}

const services = [
  { id: "geo", label: "GEO (Generative Engine Optimization)", icon: Sparkles },
  { id: "aiAds", label: "AI-Powered Paid Ads", icon: Target },
  { id: "aiContent", label: "AI Content & SEO", icon: BarChart3 },
  { id: "aiAgents", label: "AI Agents & Automation", icon: Zap },
];

const serviceMultipliers: Record<string, { leadIncrease: number; conversionImprovement: number; cpaReduction: number }> = {
  geo: { leadIncrease: 0.40, conversionImprovement: 0.15, cpaReduction: 0.20 },
  aiAds: { leadIncrease: 0.50, conversionImprovement: 0.25, cpaReduction: 0.35 },
  aiContent: { leadIncrease: 0.35, conversionImprovement: 0.20, cpaReduction: 0.15 },
  aiAgents: { leadIncrease: 0.30, conversionImprovement: 0.30, cpaReduction: 0.25 },
};

const ROICalculator = () => {
  const [step, setStep] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    services: [],
    monthlyAdSpend: 5000,
    monthlyRevenue: 50000,
    monthlyLeads: 100,
    conversionRate: 3,
    avgCustomerValue: 2000,
  });

  const steps = [
    { title: "Select Services", subtitle: "Which AI marketing services interest you?", icon: Sparkles },
    { title: "Current Ad Spend", subtitle: "How much do you invest monthly in advertising?", icon: DollarSign },
    { title: "Revenue & Leads", subtitle: "Tell us about your current performance", icon: TrendingUp },
    { title: "Conversion Metrics", subtitle: "Your conversion rate and customer value", icon: Target },
  ];

  const handleServiceToggle = (serviceId: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(s => s !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const calculateResults = () => {
    const selectedServices = formData.services.length > 0 ? formData.services : ["aiAds"];
    let totalLeadIncrease = 0, totalConversionImprovement = 0, totalCpaReduction = 0;
    
    selectedServices.forEach(service => {
      const m = serviceMultipliers[service];
      if (m) { totalLeadIncrease += m.leadIncrease; totalConversionImprovement += m.conversionImprovement; totalCpaReduction += m.cpaReduction; }
    });
    
    const dim = selectedServices.length > 1 ? 0.7 + (0.3 / selectedServices.length) : 1;
    totalLeadIncrease = Math.min(totalLeadIncrease * dim, 1.2);
    totalConversionImprovement = Math.min(totalConversionImprovement * dim, 0.6);
    totalCpaReduction = Math.min(totalCpaReduction * dim, 0.5);

    const projectedLeads = Math.round(formData.monthlyLeads * (1 + totalLeadIncrease));
    const projectedConversionRate = formData.conversionRate * (1 + totalConversionImprovement);
    const currentCustomers = (formData.monthlyLeads * formData.conversionRate) / 100;
    const projectedCustomers = (projectedLeads * projectedConversionRate) / 100;
    const currentMonthlyRevenue = currentCustomers * formData.avgCustomerValue;
    const projectedMonthlyRevenue = projectedCustomers * formData.avgCustomerValue;
    const revenueIncrease = projectedMonthlyRevenue - currentMonthlyRevenue;
    const projectedAdSpend = formData.monthlyAdSpend * (1 - totalCpaReduction);
    const adSpendSavings = formData.monthlyAdSpend - projectedAdSpend;
    const totalMonthlyGain = revenueIncrease + adSpendSavings;
    const annualGain = totalMonthlyGain * 12;
    const estimatedInvestment = selectedServices.length * 2500;
    const paybackPeriod = estimatedInvestment / totalMonthlyGain;

    return {
      currentLeads: formData.monthlyLeads, projectedLeads, leadIncrease: Math.round(totalLeadIncrease * 100),
      currentConversionRate: formData.conversionRate, projectedConversionRate: Math.round(projectedConversionRate * 10) / 10,
      currentMonthlyRevenue: Math.round(currentMonthlyRevenue), projectedMonthlyRevenue: Math.round(projectedMonthlyRevenue),
      revenueIncrease: Math.round(revenueIncrease), adSpendSavings: Math.round(adSpendSavings),
      totalMonthlyGain: Math.round(totalMonthlyGain), annualGain: Math.round(annualGain),
      estimatedInvestment, paybackPeriod: Math.round(paybackPeriod * 10) / 10, projectedAdSpend: Math.round(projectedAdSpend),
    };
  };

  const nextStep = () => { if (step < steps.length - 1) setStep(step + 1); else setShowResults(true); };
  const prevStep = () => { if (showResults) setShowResults(false); else if (step > 0) setStep(step - 1); };
  const results = calculateResults();
  const chartData = [
    { name: "Leads", current: results.currentLeads, projected: results.projectedLeads },
    { name: "Revenue (K)", current: results.currentMonthlyRevenue / 1000, projected: results.projectedMonthlyRevenue / 1000 },
  ];
  const formatCurrency = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);

  return (
    <Layout>
      <SEO title="ROI Calculator | TheCloudSEO" description="Estimate your potential returns from AI-powered marketing services." />
      <section className="relative min-h-screen pt-32 pb-20">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 max-w-4xl">
          <AnimatedSection className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <Calculator className="w-4 h-4" />ROI Calculator
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Calculate Your AI Marketing ROI</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">See how AI-powered marketing can transform your business metrics.</p>
          </AnimatedSection>

          {!showResults && (
            <div className="mb-12">
              <div className="flex justify-between mb-2">
                {steps.map((s, i) => (
                  <div key={i} className={`flex items-center gap-2 text-sm ${i <= step ? "text-primary" : "text-muted-foreground"}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all ${i < step ? "bg-primary text-primary-foreground" : i === step ? "bg-primary/20 text-primary border-2 border-primary" : "bg-muted text-muted-foreground"}`}>{i + 1}</div>
                    <span className="hidden md:block">{s.title}</span>
                  </div>
                ))}
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <motion.div className="h-full bg-gradient-to-r from-primary to-primary/80" initial={{ width: 0 }} animate={{ width: `${((step + 1) / steps.length) * 100}%` }} transition={{ duration: 0.3 }} />
              </div>
            </div>
          )}

          <AnimatePresence mode="wait">
            {!showResults ? (
              <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                <Card className="p-8 md:p-12 bg-card/50 backdrop-blur-sm border-border/50">
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 text-primary mb-4">
                      {(() => { const Icon = steps[step].icon; return <Icon className="w-8 h-8" />; })()}
                    </div>
                    <h2 className="text-2xl font-bold mb-2">{steps[step].title}</h2>
                    <p className="text-muted-foreground">{steps[step].subtitle}</p>
                  </div>

                  {step === 0 && (
                    <div className="grid gap-4 md:grid-cols-2">
                      {services.map((s) => { const Icon = s.icon; const sel = formData.services.includes(s.id); return (
                        <motion.div key={s.id} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                          <div onClick={() => handleServiceToggle(s.id)} className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${sel ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
                            <Checkbox checked={sel} /><Icon className={`w-5 h-5 ${sel ? "text-primary" : "text-muted-foreground"}`} /><span className="font-medium">{s.label}</span>
                          </div>
                        </motion.div>
                      ); })}
                    </div>
                  )}

                  {step === 1 && (
                    <div className="space-y-8">
                      <div><div className="flex justify-between mb-4"><Label className="text-base">Monthly Ad Spend</Label><span className="text-2xl font-bold text-primary">{formatCurrency(formData.monthlyAdSpend)}</span></div>
                        <Slider value={[formData.monthlyAdSpend]} onValueChange={([v]) => setFormData({ ...formData, monthlyAdSpend: v })} min={1000} max={100000} step={500} className="mb-4" />
                        <div className="flex justify-between text-sm text-muted-foreground"><span>$1,000</span><span>$100,000</span></div>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-8">
                      <div><div className="flex justify-between mb-4"><Label className="text-base">Monthly Revenue</Label><span className="text-2xl font-bold text-primary">{formatCurrency(formData.monthlyRevenue)}</span></div>
                        <Slider value={[formData.monthlyRevenue]} onValueChange={([v]) => setFormData({ ...formData, monthlyRevenue: v })} min={10000} max={1000000} step={5000} />
                      </div>
                      <div><div className="flex justify-between mb-4"><Label className="text-base">Monthly Leads</Label><span className="text-2xl font-bold text-primary">{formData.monthlyLeads}</span></div>
                        <Slider value={[formData.monthlyLeads]} onValueChange={([v]) => setFormData({ ...formData, monthlyLeads: v })} min={10} max={1000} step={10} />
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-8">
                      <div><div className="flex justify-between mb-4"><Label className="text-base">Conversion Rate</Label><span className="text-2xl font-bold text-primary">{formData.conversionRate}%</span></div>
                        <Slider value={[formData.conversionRate]} onValueChange={([v]) => setFormData({ ...formData, conversionRate: v })} min={0.5} max={20} step={0.5} />
                      </div>
                      <div><div className="flex justify-between mb-4"><Label className="text-base">Average Customer Value</Label><span className="text-2xl font-bold text-primary">{formatCurrency(formData.avgCustomerValue)}</span></div>
                        <Slider value={[formData.avgCustomerValue]} onValueChange={([v]) => setFormData({ ...formData, avgCustomerValue: v })} min={100} max={50000} step={100} />
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between mt-10">
                    <Button variant="outline" onClick={prevStep} disabled={step === 0} className="gap-2"><ArrowLeft className="w-4 h-4" />Back</Button>
                    <Button onClick={nextStep} className="gap-2">{step === steps.length - 1 ? "Calculate ROI" : "Next"}<ArrowRight className="w-4 h-4" /></Button>
                  </div>
                </Card>
              </motion.div>
            ) : (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="space-y-8">
                <Card className="p-8 md:p-12 bg-gradient-to-br from-primary/10 via-card to-card border-primary/20">
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary text-sm font-medium mb-4"><TrendingUp className="w-4 h-4" />Your Projected Results</div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-2"><span className="text-primary"><AnimatedCounter end={results.annualGain} prefix="$" /></span> Annual Gain</h2>
                  </div>
                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="text-center p-6 rounded-xl bg-background/50"><p className="text-sm text-muted-foreground mb-1">Monthly Revenue Increase</p><p className="text-3xl font-bold text-green-500">+<AnimatedCounter end={results.revenueIncrease} prefix="$" /></p></div>
                    <div className="text-center p-6 rounded-xl bg-background/50"><p className="text-sm text-muted-foreground mb-1">Lead Increase</p><p className="text-3xl font-bold text-blue-500">+<AnimatedCounter end={results.leadIncrease} suffix="%" /></p></div>
                    <div className="text-center p-6 rounded-xl bg-background/50"><p className="text-sm text-muted-foreground mb-1">Payback Period</p><p className="text-3xl font-bold text-primary">{results.paybackPeriod} mo</p></div>
                  </div>
                  <div className="h-64 mb-8">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} barCategoryGap="20%">
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                        <YAxis stroke="hsl(var(--muted-foreground))" />
                        <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                        <Bar dataKey="current" name="Current" radius={[4, 4, 0, 0]}>{chartData.map((_, i) => <Cell key={i} fill="hsl(var(--muted-foreground))" />)}</Bar>
                        <Bar dataKey="projected" name="Projected" radius={[4, 4, 0, 0]}>{chartData.map((_, i) => <Cell key={i} fill="hsl(var(--primary))" />)}</Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
                <Card className="p-8 text-center bg-card/50 backdrop-blur-sm border-border/50">
                  <h3 className="text-2xl font-bold mb-4">Ready to Achieve These Results?</h3>
                  <p className="text-muted-foreground mb-6 max-w-lg mx-auto">Book a free strategy call to discuss how we can help you reach these projections.</p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button asChild size="lg" className="gap-2"><a href="https://calendly.com/thecloudseo/30min" target="_blank" rel="noopener noreferrer">Book Strategy Call<ArrowRight className="w-4 h-4" /></a></Button>
                    <Button variant="outline" onClick={prevStep} className="gap-2"><ArrowLeft className="w-4 h-4" />Recalculate</Button>
                  </div>
                </Card>
                <div className="grid md:grid-cols-3 gap-4">
                  <Link to="/services" className="group"><Card className="p-6 h-full transition-all hover:border-primary/50 hover:bg-primary/5"><h4 className="font-semibold mb-2 group-hover:text-primary transition-colors">Explore Services</h4><p className="text-sm text-muted-foreground">Learn about our AI marketing solutions</p></Card></Link>
                  <Link to="/pricing" className="group"><Card className="p-6 h-full transition-all hover:border-primary/50 hover:bg-primary/5"><h4 className="font-semibold mb-2 group-hover:text-primary transition-colors">View Pricing</h4><p className="text-sm text-muted-foreground">See our transparent pricing options</p></Card></Link>
                  <Link to="/ai-agents" className="group"><Card className="p-6 h-full transition-all hover:border-primary/50 hover:bg-primary/5"><h4 className="font-semibold mb-2 group-hover:text-primary transition-colors">AI Agents</h4><p className="text-sm text-muted-foreground">Discover custom AI automation</p></Card></Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </Layout>
  );
};

export default ROICalculator;